import React, { useState, useEffect } from 'react';
import { Layout, Typography, Card, Flex, theme, Spin, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { HttpRequest } from '../../../../../../utils/httpClient';

import { createLogger } from '../../../../../../utils/logger';
const logger = createLogger('features-assistants');

const { Content } = Layout;
const { Title, Text } = Typography;
const { useToken } = theme;

interface IntegrationFunctionsProps {
  integration: {
    name: string;
    logo: string;
    id: string;
  };
  onBack: () => void;
  onFunctionSelect: (functionData: any) => void;
}

export const IntegrationFunctions: React.FC<IntegrationFunctionsProps> = ({
  integration,
  onBack,
  onFunctionSelect,
}) => {
  const { token } = useToken();
  const [functions, setFunctions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFunctions = async () => {
      try {
        const request = new HttpRequest(`${process.env.REACT_APP_API_HOST}/integration/functions`);
        const response = await request.get(`${integration.id}`);
        logger.info('API Response:', response.data);

        // Asegurarnos de que las funciones tengan la estructura correcta
        const formattedFunctions = (response.data.functions || []).map((func: any) => {
          // Asegurarnos de que los campos sean un array
          const fields = Array.isArray(func.fields)
            ? func.fields
            : Array.isArray(func.parameters)
              ? func.parameters.map((param: any) => ({
                  name: param.name || '',
                  label: param.label || param.name || '',
                  type: param.type || 'input',
                  placeholder: param.placeholder || `Enter ${param.name}`,
                  required: param.required || false,
                }))
              : [];

          return {
            ...func,
            fields,
            key: func.key || func.id || func.name,
            title: func.title || func.name || '',
            description: func.description || '',
          };
        });

        logger.info('Formatted Functions:', formattedFunctions);
        setFunctions(formattedFunctions);
      } catch (error) {
        logger.error('Error fetching integration functions:', error);
        message.error('Failed to load integration functions');
      } finally {
        setLoading(false);
      }
    };

    fetchFunctions();
  }, [integration.id]);

  const handleFunctionClick = (func: any) => {
    // Asegurarnos de que la función tenga la estructura esperada
    const formattedFunction = {
      ...func,
      integration: {
        name: integration.name,
        logo: integration.logo,
        id: integration.id,
      },
    };

    logger.info('Selected function:', formattedFunction);
    onFunctionSelect(formattedFunction);
  };

  if (loading) {
    return (
      <Layout style={{ background: token.colorBgContainer }}>
        <Content>
          <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
            <Spin size="large" />
          </Flex>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ background: token.colorBgContainer }}>
      <Content>
        <Flex vertical gap={token.marginMD} style={{ padding: token.padding }}>
          <Flex align="center" gap={token.marginSM}>
            <ArrowLeftOutlined onClick={onBack} style={{ cursor: 'pointer' }} />
            <Title level={4} style={{ margin: 0 }}>
              {integration.name} Integration
            </Title>
          </Flex>

          <Flex gap={token.marginMD} wrap="wrap">
            {functions.map((func) => (
              <Card
                key={func.key || func.id || func.name}
                hoverable
                style={{
                  width: 300,
                  marginBottom: token.marginMD,
                }}
                onClick={() => {
                  logger.info('Card clicked:', func);
                  handleFunctionClick(func);
                }}
              >
                <Flex vertical align="center">
                  <div
                    style={{
                      width: '100%',
                      height: '60px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginBottom: token.marginSM,
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={integration.logo}
                      alt="Integration logo"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </div>
                  <Title level={5} style={{ margin: 0, textAlign: 'center' }}>
                    {func.title || func.name}
                  </Title>
                  <Text type="secondary" style={{ textAlign: 'center' }}>
                    {func.description || ''}
                  </Text>
                </Flex>
              </Card>
            ))}
          </Flex>
        </Flex>
      </Content>
    </Layout>
  );
};
